import type { Id, Op, Transaction } from '@vp/shared-types';
import { invertOps, type CanvasDocument } from './document';

export interface ModuleAvailability {
  /** Module is installed, enabled and running. */
  isModuleAvailable(moduleId: string): boolean;
  /** Current format version of a module object type, if known. */
  typeFormatVersion(type: string): number | undefined;
}

export class UndoBlockedError extends Error {
  readonly code = 'E_UNDO_BLOCKED';
  readonly moduleId: string;
  readonly reason: 'unavailable' | 'incompatible';
  constructor(moduleId: string, reason: 'unavailable' | 'incompatible') {
    super(
      reason === 'unavailable'
        ? `Step cannot be undone while module ${moduleId} is unavailable`
        : `Step was recorded with an incompatible data format of module ${moduleId}`,
    );
    this.moduleId = moduleId;
    this.reason = reason;
  }
}

interface Entry {
  tx: Transaction;
  bytes: number;
}

function estimateBytes(ops: Op[]): number {
  // Rough but cheap size estimate; JSON length of a sample scaled to op count.
  let total = 0;
  for (const op of ops) {
    if (op.t === 'update') total += 64 + JSON.stringify(op.after).length * 2;
    else total += 64 + JSON.stringify(op.rec).length;
  }
  return total;
}

/**
 * Undo/redo over committed transactions (§12.1).
 * Undo applies inverse ops as a new transaction (journalled with `undoOf`).
 */
export class History {
  private undoStack: Entry[] = [];
  private redoStack: Entry[] = [];
  private bytes = 0;
  private listeners = new Set<() => void>();
  private unsub: () => void;

  constructor(
    private readonly doc: CanvasDocument,
    private readonly availability: ModuleAvailability,
    private readonly maxBytes = 64 * 1024 * 1024,
  ) {
    this.unsub = doc.onCommit((tx, meta) => {
      if (meta.history) return;
      this.push(tx);
    });
  }

  dispose(): void {
    this.unsub();
  }

  onChange(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(): void {
    for (const fn of this.listeners) fn();
  }

  private push(tx: Transaction): void {
    const bytes = estimateBytes(tx.ops);
    this.undoStack.push({ tx, bytes });
    this.bytes += bytes;
    for (const e of this.redoStack) this.bytes -= e.bytes;
    this.redoStack = [];
    while (this.bytes > this.maxBytes && this.undoStack.length > 1) {
      const dropped = this.undoStack.shift()!;
      this.bytes -= dropped.bytes;
    }
    this.emit();
  }

  /** Append ops produced asynchronously (e.g. late recompute) to an existing step. */
  amend(txId: Id, ops: Op[]): boolean {
    const e = this.undoStack.find((x) => x.tx.id === txId);
    if (!e) return false;
    e.tx = { ...e.tx, ops: [...e.tx.ops, ...ops] };
    const extra = estimateBytes(ops);
    e.bytes += extra;
    this.bytes += extra;
    return true;
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }
  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }
  get size(): { undo: number; redo: number; bytes: number } {
    return { undo: this.undoStack.length, redo: this.redoStack.length, bytes: this.bytes };
  }

  /** Why the next undo is blocked, or null (§12.3). */
  undoBlocker(): UndoBlockedError | null {
    const e = this.undoStack[this.undoStack.length - 1];
    return e ? this.blocker(e.tx) : null;
  }
  redoBlocker(): UndoBlockedError | null {
    const e = this.redoStack[this.redoStack.length - 1];
    return e ? this.blocker(e.tx) : null;
  }

  private blocker(tx: Transaction): UndoBlockedError | null {
    for (const m of tx.modules) {
      if (!this.availability.isModuleAvailable(m)) return new UndoBlockedError(m, 'unavailable');
    }
    for (const op of tx.ops) {
      const rec = op.t === 'update' ? this.doc.get(op.id) : op.rec;
      if (!rec || op.table !== 'objects') continue;
      const type = rec['type'];
      const fv = rec['formatVersion'];
      if (typeof type !== 'string' || type.startsWith('core:')) continue;
      const cur = this.availability.typeFormatVersion(type);
      if (cur !== undefined && typeof fv === 'number' && fv !== cur) {
        return new UndoBlockedError(type.slice(0, type.lastIndexOf(':')), 'incompatible');
      }
    }
    return null;
  }

  undo(): Transaction | null {
    const e = this.undoStack[this.undoStack.length - 1];
    if (!e) return null;
    const blocked = this.blocker(e.tx);
    if (blocked) throw blocked;
    const applied = this.doc.applyOps(invertOps(e.tx.ops), { source: e.tx.source, label: e.tx.label, undoOf: e.tx.id }, { history: true });
    this.undoStack.pop();
    this.redoStack.push(e);
    this.emit();
    return applied;
  }

  redo(): Transaction | null {
    const e = this.redoStack[this.redoStack.length - 1];
    if (!e) return null;
    const blocked = this.blocker(e.tx);
    if (blocked) throw blocked;
    const applied = this.doc.applyOps(e.tx.ops, { source: e.tx.source, label: e.tx.label, redoOf: e.tx.id }, { history: true });
    this.redoStack.pop();
    this.undoStack.push(e);
    this.emit();
    return applied;
  }

  clear(): void {
    this.undoStack = [];
    this.redoStack = [];
    this.bytes = 0;
    this.emit();
  }
}
